<template>
  <div :style="{ border: isFocus ? '0.5px solid rgba(255, 255, 255, 0.6)' : '' }" class="menu-fill flex items-center px-2 relative">
    <div class="flex items-center w-[90px]">
      <popover v-model="visible" :visible-arrow="false" placement="left-start" popper-class="menu-fill-popper"
        trigger="click" @after-enter="onPopperShow">
        <div slot="reference" class="header-look rounded cursor-pointer overflow-hidden">
          <div :style="{ background: target[colorKey] }" class="inner w-full h-full" />
        </div>
        <div v-if="visible">
          <color-model ref="colorModel" :solid-color="solidColor" :value="target[colorKey]" @change="onColorModelChange"
            @close="onColorModelClose" />
        </div>
      </popover>
      <input ref="colorValue" v-model="colorValue" class="header-name text-textColor-3 text-12 ml-2"
        :class="{ 'is-liner': isLiner }" @input="onColorValueChange" @click="handleValueClick" @focus="handleValueFocus"
        @blur="handleValueBlur" />
    </div>
    <div v-if="!isLiner" class="opacity-input">
      <el-input v-model="alpha" class="w-full" size="mini" @blur="onAlphaChange" @focus="onAlphaFocus"
        @keyup.native="onAlphaKeyup" />
    </div>
    <div v-if="eye && hasEyeKey()"
      class="eye-change flex items-center justify-center absolute right-0 top-0 text-textColor-7 hover:text-textColor-5 hover:bg-fillColor-2 cursor-pointer"
      @click="handleToggleVisible">
      <component :is="isShow ? 'icon-show' : 'icon-hide'" class="text-20" />
    </div>
  </div>
</template>

<script>
import { Popover, Input, Tooltip } from 'element-ui'
import { debounce } from 'lodash-es'
import { colord } from "colord";
import iconShow from '@/assets/svg/icon/iconShow.svg'
import iconHide from '@/assets/svg/icon/iconHide.svg'
import colorModel from "~/components/colorModel";
import { getAlpha } from "~/utils/colors";
import { insertStep } from "~/utils/event";

export default {
  components: {
    Popover,
    ElInput: Input,
    ElTooltip: Tooltip,
    iconShow,
    iconHide,
    colorModel
  },
  props: {
    colorKey: {
      type: String,
      default: 'backgroundColor'
    },
    eyeKey: {
      type: String,
      default: 'backgroundColorShow'
    },
    targetKey: {
      type: String,
      default: ''
    },
    targetIndex: {
      type: [Number, String],
      default: ''
    },
    eye: {
      type: Boolean,
      default: true
    },
    target: {
      type: Object,
      default() {
        return {}
      }
    },
    model: {
      type: Object,
      default() {
        return {}
      }
    },
    solidColor: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      alpha: this.value,
      isFocus: false,
      colorValue: '',
      oldValue: false
    }
  },
  computed: {
    colorName() {
      return this.isLiner ? '渐变色' : colord(this.target[this.colorKey]).toHex()
    },
    isLiner() {
      const value = this.target[this.colorKey] || ''
      return value.includes('linear-gradient')
    },
    isShow() {
      return this.target[this.eyeKey]
    },
    currentColor() {
      return this.target[this.colorKey]
    }
  },
  watch: {
    currentColor() {
      this.colorValue = String(this.colorName)
    }
  },
  mounted() {
    this.colorValue = String(this.colorName)
    this.formatAlpha()
    this.SiteMenu.$bus.$on('updateAlpha', () => {
      this.formatAlpha()
    })
  },
  methods: {
    onAlphaChange(event) {
      this.isFocus = false;
      const value = event.target.value.replace('%', '')
      const color = colord(this.target[this.colorKey])
      const rgba = color.alpha(value / 100).toRgbString()
      if (!this.alpha.includes('%')) {
        this.alpha += '%'
      }
      if (!this.isLiner) {
        this.setValue(rgba)
      }
    },
    onAlphaFocus(event) {
      this.isFocus = true;
      event.target.setSelectionRange(0, event.target.value.length)
    },
    onAlphaKeyup() {
      this.alpha = this.alpha.replace(/[^\d^.%]/g, '')
    },
    onPopperShow() {
      this.$refs.colorModel && this.$refs.colorModel.init()
    },
    handleToggleVisible() {
      const { eyeKey } = this
      insertStep(this, [this.targetKey, this.targetIndex, this.eyeKey], this.target[eyeKey])
      this.target[eyeKey] = !this.target[eyeKey]
    },
    hasEyeKey() {
      return this.eyeKey && Object.prototype.hasOwnProperty.call(this.target, this.eyeKey)
    },
    formatAlpha() {
      const alpha = getAlpha(this.target[this.colorKey])
      this.alpha = typeof alpha === 'number' ? Math.round(alpha * 100) + '%' : '100%'
    },
    onColorModelClose() {
      this.visible = false
    },
    handleValueClick() {
      this.$refs.colorValue.setSelectionRange(0, 999)
      const color = colord(this.colorValue)
      if (color.isValid()) this.oldValue = this.colorValue
    },
    onColorValueChange: debounce(function (e) {
      const value = e.target.value
      if (!this.solidColor && (value.includes('linear-gradient') || value.includes('radial-gradient'))) {
        this.setValue(value)
        this.$emit('change', value)
        return
      }

      const color = colord(value)
      if (color.isValid()) {
        const newValue = color.toRgbString()
        this.setValue(newValue)
        this.colorValue = color.toHex().replace('#', '')
        this.formatAlpha()
        this.$emit('change', newValue)
      } else {
        this.colorValue = this.oldValue
      }
    }, 500),
    setValue(value) {
      const oldValue = this.target[this.colorKey]
      insertStep(this, [this.targetKey, this.targetIndex, this.colorKey], oldValue)
      if (this.colorKey !== 'backgroundColor') {
        this.target[this.colorKey] = value
      } else if (this.target && this.target.setBackgroundColor) {
        this.target.setBackgroundColor(value, this.colorKey)
      } else {
        this.target[this.colorKey] = value
      }
    },
    debounceSetValue: debounce(function (color) {
      this.setValue(color)
      this.formatAlpha()
      this.$emit('change', color)
    }, 500),
    onColorModelChange(type, color) {
      this.setValue(color)
      this.formatAlpha()
      this.$emit('change', color)
    },
    handleValueFocus() {
      if (this.isLiner) {
        this.colorValue = this.target[this.colorKey]
      }
    },
    handleValueBlur() {
      if (this.isLiner) {
        this.colorValue = this.colorName
      }
    }
  }
}
</script>

<style lang="less">
@imgBg: "data:image/svg+xml;utf8,%3Csvg%20width%3D%226%22%20height%3D%226%22%20viewBox%3D%220%200%206%206%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M0%200H3V3H0V0Z%22%20fill%3D%22rgba(255,255,255,0.05)%22/%3E%3Cpath%20d%3D%22M3%200H6V3H3V0Z%22%20fill%3D%22rgba(255,255,255,0.1)%22/%3E%3Cpath%20d%3D%22M3%203H6V6H3V3Z%22%20fill%3D%22rgba(255,255,255,0.05)%22/%3E%3Cpath%20d%3D%22M0%203H3V6H0V3Z%22%20fill%3D%22rgba(255,255,255,0.1)%22/%3E%3C/svg%3E%0A";

.menu-fill-popper {
  padding: 0;
  border: 1px solid var(--border-color-base) !important;
  margin-right: 12px !important;
  background: var(--bg-color-overlay) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: var(--shadow-lg) !important;
}

.menu-fill {
  height: 36px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;

  &:hover,
  &:focus-within {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .header-look {
    width: 20px;
    height: 20px;
    background-image: url(@imgBg);
    border-radius: 4px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.1);
    }

    .inner {
      border-radius: 4px;
    }
  }

  .header-name {
    background-color: transparent;
    outline: none;
    width: 70px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8) !important;
    font-family: monospace;

    &.is-liner {
      width: 130px;
    }
  }

  .opacity-input {
    width: 46px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      height: 12px;
      width: 1px;
      background: rgba(255, 255, 255, 0.1);
    }

    .el-input__inner {
      padding: 0 0 0 10px;
      background-color: transparent;
      border: 0;
      color: rgba(255, 255, 255, 0.6);
      font-size: 12px;
      height: 36px;
      line-height: 36px;
      text-align: center;

      &:focus {
        color: white;
      }
    }
  }

  .eye-change {
    width: 32px;
    height: 100%;
    border-radius: 0 8px 8px 0;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white !important;
    }
  }
}
</style>
